"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Gate from "./Gate";
import LoginScreen from "./LoginScreen";
import SignupForm from "./SignupForm";
import CharacterSelect from "./CharacterSelect";
import CharacterNew from "./CharacterNew";
import WorldCreateScreen from "./WorldCreateScreen";
import WorldReviewScreen from "./WorldReviewScreen";
import DeveloperSwitchForm from "./DeveloperSwitchForm";
import WorldAdminScreen from "./WorldAdminScreen";
import WorldPlayScreen from "./WorldPlayScreen";
import { AccountView, CharacterEntry } from "@/lib/types";

const REFRESH_MS = 30000;

type View = "login" | "signup" | "select" | "new" | "world-create" | "world-review" | "developer" | "world-admin" | "play";

/** 世界に入る前の画面を切り替える */
export default function AuthScreen({ initialAccount }: { initialAccount: AccountView | null }) {
  const [account, setAccount] = useState<AccountView | null>(initialAccount);
  const [view, setView] = useState<View>(initialAccount ? "select" : "login");
  const [world, setWorld] = useState("");
  const [region, setRegion] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [slug, setSlug] = useState("");

  const enter = useCallback((next: AccountView) => {
    setAccount(next);
    setView("select");
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setAccount(null);
    setView("login");
  }, []);

  const openWorldAdmin = useCallback((chosen: string) => {
    setWorld(chosen);
    setView("world-admin");
  }, []);

  const openWorldCreate = useCallback((chosenWorld: string, chosenRegion: string) => {
    setWorld(chosenWorld);
    setRegion(chosenRegion);
    setRequestId(null);
    setView("world-create");
  }, []);

  // キャラクター選択画面に居る間は、枠の状態（依頼中・進行中・稼働中）を読み直す
  useEffect(() => {
    if (view !== "select" || account === null) return;
    let alive = true;
    const refresh = () => {
      fetch("/api/auth/session")
        .then((response) => response.json())
        .then((body) => {
          if (alive && body.account) setAccount(body.account as AccountView);
        })
        .catch(() => {
          // 読めない間は今の表示を保つ
        });
    };
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [view, account === null]);

  const redoRequest = useCallback((entry: CharacterEntry) => {
    setWorld(entry.world);
    setRegion(entry.region);
    setRequestId(entry.request ?? null);
    setView("world-create");
  }, []);

  // 出している画面の名。ログインしていない間は login に、開けない開発者の画面は select に寄せる
  const shown: View =
    view === "signup"
      ? "signup"
      : view === "login" || account === null
        ? "login"
        : (view === "developer" || view === "world-admin") && !account.canOpenAdmin
          ? "select"
          : account.maintenance
            ? "select"
            : view;

  // 最初に出す画面は待たずに出す。画面を切り替えた後は、切り替えるたびにロード画面を挟む
  const first = useRef(shown);
  const [moved, setMoved] = useState(false);
  useEffect(() => {
    if (shown !== first.current) setMoved(true);
  }, [shown]);

  let screen: React.ReactNode;
  if (shown === "signup") {
    screen = <SignupForm onCreated={enter} onCancel={() => setView("login")} />;
  } else if (shown === "login" || account === null) {
    screen = <LoginScreen onLoggedIn={enter} onCreateAccount={() => setView("signup")} />;
  } else if (shown === "new") {
    screen = <CharacterNew account={account} onDecide={openWorldCreate} onCancel={() => setView("select")} onLogout={logout} />;
  } else if (shown === "world-create") {
    screen = (
      <WorldCreateScreen
        world={world}
        region={region}
        account={account}
        requestId={requestId}
        onBack={() => setView(requestId === null ? "new" : "select")}
        onDone={(next) => {
          if (next) setAccount(next);
          setView("select");
        }}
      />
    );
  } else if (shown === "world-review") {
    screen = (
      <WorldReviewScreen
        world={world}
        region={region}
        slug={slug}
        account={account}
        onBack={() => setView("select")}
        onDone={(next) => {
          if (next) setAccount(next);
          setView("select");
        }}
      />
    );
  } else if (shown === "play") {
    screen = <WorldPlayScreen world={world} region={region} slug={slug} account={account} onExit={() => setView("select")} onLogout={logout} />;
  } else if (shown === "developer") {
    screen = <DeveloperSwitchForm onDecide={openWorldAdmin} onCancel={() => setView("select")} />;
  } else if (shown === "world-admin") {
    screen = <WorldAdminScreen world={world} onBack={() => setView("developer")} />;
  } else {
    screen = (
      <CharacterSelect
        account={account}
        onLogout={logout}
        onNew={() => setView("new")}
        onRedo={redoRequest}
        onReview={(entry) => {
          setWorld(entry.world);
          setRegion(entry.region);
          setSlug(entry.slug);
          setView("world-review");
        }}
        onSelect={(entry) => {
          setWorld(entry.world);
          setRegion(entry.region);
          setSlug(entry.slug);
          setView("play");
        }}
        onDeveloperSwitch={() => setView("developer")}
      />
    );
  }

  return (
    <Gate key={shown} variant="screen" open={!moved && shown === first.current}>
      {screen}
    </Gate>
  );
}
